import { Reference } from "@apollo/client";

import useMutationWithValidation from "@hooks/useMutationWithValidation";
import {
  GetJourneyWithPlanQuery,
  GoalDestroyDocument,
  GoalDestroyMutation,
  GoalDestroyMutationVariables
} from "@typing/Generated";
import { getAllGoals } from "@utils/modelUtils";

const useDestroyGoal = (journey: GetJourneyWithPlanQuery["journey"]) => {
  const [destroyGoalMutation] = useMutationWithValidation<GoalDestroyMutation, GoalDestroyMutationVariables>(
    GoalDestroyDocument,
    "goal",
    {
      update: (store, { data }) => {
        const goal = getAllGoals(journey).find(goal => goal.id === data?.goalDestroy?.id);

        if (!journey || !goal) return;

        //
        // For each of the various models on the goal, we need to remove it is only connected to one goal
        // Otherwise we need to remove the goal from the model
        //
        goal.carePlanActionItems.map(actionItem => {
          store.modify({
            fields: {
              carePlanActionItems(cachedRefs: readonly Reference[], { readField }) {
                return cachedRefs.filter(ref => actionItem.id !== readField("id", ref));
              }
            },
            id: store.identify({ ...journey })
          });
        });

        goal.tasks.map(task => {
          store.modify({
            fields: {
              clientVisibleTasks(cachedRefs: readonly Reference[], { readField }) {
                return cachedRefs.filter(ref => task.id !== readField("id", ref));
              }
            },
            id: store.identify({ ...journey })
          });
        });

        goal.worksheets.map(worksheet => {
          store.modify({
            fields: {
              worksheets(cachedRefs: readonly Reference[], { readField }) {
                return cachedRefs.filter(ref => worksheet.id !== readField("id", ref));
              }
            },
            id: store.identify({ ...journey })
          });
        });

        // Remove the goal from the journey
        store.modify({
          fields: {
            editModeGoals(cachedRefs: readonly Reference[], { readField }) {
              return cachedRefs.filter(ref => goal.id !== readField("id", ref));
            },
            goals(cachedRefs: readonly Reference[], { readField }) {
              return cachedRefs.filter(
                ref => goal.id !== readField("id", ref) && goal.activeGoalId !== readField("id", ref)
              );
            }
          },
          id: store.identify({ ...journey })
        });
      }
    }
  );
  return destroyGoalMutation;
};

export default useDestroyGoal;
